import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Edit, 
  Search, 
  PlusCircle, 
  X, 
  Filter, 
  ChevronLeft, 
  ChevronRight, 
  AlertTriangle,
  Info
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/auth';
import { apiClient } from '@/api/client';
import { 
  getRoleDisplayName, 
  getRoleBadgeVariant 
} from '@/utils/role-utils';

interface SuperAdminPermissionsTabProps {
  users: any[];
  restaurants: any[];
  organisations: any[];
  permissions: {
    organisationAccess: any[];
    restaurantAccess: any[];
  };
}

const SuperAdminPermissionsTab: React.FC<SuperAdminPermissionsTabProps> = ({ 
  users: initialUsers,
  restaurants,
  organisations,
  permissions: initialPermissions 
}) => {
  const { user: currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('restaurant');
  
  // Permissions state - initialize with empty arrays to avoid undefined issues
  // Use formatted data from initialPermissions if available
  const [permissions, setPermissions] = useState({
    restaurantAccess: [],
    organisationAccess: []
  });

  // Use effect to enrich the initial permissions from props with user and organization names
  useEffect(() => {
    const enrichInitialPermissions = async () => {
      if (!initialPermissions?.restaurantAccess?.length && !initialPermissions?.organisationAccess?.length) {
        return;
      }

      console.log("Using initial permissions from props:", initialPermissions);

      try {
        // Create a map of user IDs to fetch once for all permissions
        const userIds = new Set<string>();
        // Create a map of restaurant IDs to fetch once
        const restaurantIds = new Set<string>();
        // Create a map of organisation IDs to fetch once
        const organisationIds = new Set<string>();

        // Collect all unique IDs from restaurant access records
        if (Array.isArray(initialPermissions.restaurantAccess)) {
          initialPermissions.restaurantAccess.forEach(access => {
            if (access.user_id) userIds.add(access.user_id);
            if (access.restaurant_id) restaurantIds.add(access.restaurant_id);
            if (access.organisation_id) organisationIds.add(access.organisation_id);
          });
        }

        // Collect all unique IDs from organisation access records
        if (Array.isArray(initialPermissions.organisationAccess)) {
          initialPermissions.organisationAccess.forEach(access => {
            if (access.user_id) userIds.add(access.user_id);
            if (access.organisation_id) organisationIds.add(access.organisation_id);
          });
        }

        // Fetch all users in one query
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('id, name, email, role')
          .in('id', Array.from(userIds));

        if (usersError) {
          console.error("Error fetching users for permissions:", usersError);
        }

        // Fetch all restaurants in one query
        const { data: restaurantsData, error: restaurantsError } = await supabase
          .from('restaurants_master')
          .select('restaurant_id, restaurant_name, organisation_id')
          .in('restaurant_id', Array.from(restaurantIds));

        if (restaurantsError) {
          console.error("Error fetching restaurants for permissions:", restaurantsError);
        }

        // Fetch all organisations in one query
        const { data: organisationsData, error: organisationsError } = await supabase
          .from('organisations')
          .select('id, name')
          .in('id', Array.from(organisationIds));

        if (organisationsError) {
          console.error("Error fetching organisations for permissions:", organisationsError);
        }

        // Create lookup maps for faster access
        const userMap = (usersData || []).reduce((map, user) => {
          map[user.id] = user;
          return map;
        }, {});

        const restaurantMap = (restaurantsData || []).reduce((map, restaurant) => {
          map[restaurant.restaurant_id] = restaurant;
          return map;
        }, {});

        const organisationMap = (organisationsData || []).reduce((map, org) => {
          map[org.id] = org;
          return map;
        }, {});

        // Enrich restaurant access records with names
        const enrichedRestaurantAccess = Array.isArray(initialPermissions.restaurantAccess)
          ? initialPermissions.restaurantAccess.map(access => {
              const user = userMap[access.user_id] || {};
              const restaurant = restaurantMap[access.restaurant_id] || {};
              const organisation = organisationMap[access.organisation_id] || 
                                   organisationMap[restaurant.organisation_id] || {};
              
              return {
                id: access.id,
                user_id: access.user_id,
                user_name: user.name || 'Unknown User',
                user_email: user.email || '',
                user_role: user.role || 'user',
                restaurant_id: access.restaurant_id,
                restaurant_name: restaurant.restaurant_name || 'Unknown Restaurant',
                organisation_id: access.organisation_id || restaurant.organisation_id,
                organisation_name: organisation.name || '',
                access_level: access.access_level
              };
            })
          : [];

        // Enrich organisation access records with names
        const enrichedOrganisationAccess = Array.isArray(initialPermissions.organisationAccess)
          ? initialPermissions.organisationAccess.map(access => {
              const user = userMap[access.user_id] || {};
              const organisation = organisationMap[access.organisation_id] || {};
              
              return {
                id: access.id,
                user_id: access.user_id,
                user_name: user.name || 'Unknown User',
                user_email: user.email || '',
                user_role: user.role || 'user',
                organisation_id: access.organisation_id,
                organisation_name: organisation.name || 'Unknown Organisation',
                access_level: access.access_level
              };
            })
          : [];

        console.log("Enriched restaurant access:", enrichedRestaurantAccess);
        console.log("Enriched organisation access:", enrichedOrganisationAccess);

        // Update the permissions state with enriched data
        setPermissions({
          restaurantAccess: enrichedRestaurantAccess,
          organisationAccess: enrichedOrganisationAccess
        });
      } catch (err) {
        console.error("Error enriching initial permissions:", err);
      }
    };

    // Only run if we have initial permissions
    if (initialPermissions?.restaurantAccess?.length > 0 || initialPermissions?.organisationAccess?.length > 0) {
      enrichInitialPermissions();
    }
  }, [initialPermissions]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(!initialPermissions);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [grantRestaurantDialogOpen, setGrantRestaurantDialogOpen] = useState(false);
  const [grantOrganisationDialogOpen, setGrantOrganisationDialogOpen] = useState(false);
  const [confirmRemoveDialogOpen, setConfirmRemoveDialogOpen] = useState(false);
  const [selectedAccessToRemove, setSelectedAccessToRemove] = useState<{
    id: string;
    type: 'restaurant' | 'organisation';
    name: string;
    userName: string;
  } | null>(null);
  
  // Granting form states
  const [grantRestaurantForm, setGrantRestaurantForm] = useState({
    user_id: '',
    restaurant_id: '',
    access_level: 'admin',
    multiple_users: false,
    multiple_restaurants: false,
    user_ids: [] as string[],
    restaurant_ids: [] as string[]
  });
  
  const [grantOrganisationForm, setGrantOrganisationForm] = useState({
    user_id: '',
    organisation_id: '',
    access_level: 'admin',
    multiple_users: false,
    user_ids: [] as string[]
  });
  
  // Users for form dropdowns
  const filteredUsers = useMemo(() => {
    return initialUsers;
  }, [initialUsers]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filtered permissions based on search and role filter
  const filteredRestaurantAccess = useMemo(() => {
    return permissions.restaurantAccess.filter(access => {
      const matchesSearch = 
        searchQuery === '' || 
        access.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        access.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        access.restaurant_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRole = roleFilter === 'all' || access.user_role === roleFilter;
      
      return matchesSearch && matchesRole;
    });
  }, [permissions.restaurantAccess, searchQuery, roleFilter]);
  
  const filteredOrganisationAccess = useMemo(() => {
    return permissions.organisationAccess.filter(access => {
      const matchesSearch = 
        searchQuery === '' || 
        access.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        access.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        access.organisation_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRole = roleFilter === 'all' || access.user_role === roleFilter;
      
      return matchesSearch && matchesRole;
    });
  }, [permissions.organisationAccess, searchQuery, roleFilter]);
  
  // Helper to check if a user already has access
  const hasExistingAccess = useCallback((userId, type) => {
    if (type === 'restaurant') {
      return permissions.restaurantAccess.some(access => access.user_id === userId);
    } else {
      return permissions.organisationAccess.some(access => access.user_id === userId);
    }
  }, [permissions]);
  
  // Get paginated data
  const paginatedData = useMemo(() => {
    const filteredData = activeTab === 'restaurant' 
      ? filteredRestaurantAccess 
      : filteredOrganisationAccess;
    
    const total = filteredData.length;
    setTotalItems(total);
    
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [activeTab, filteredRestaurantAccess, filteredOrganisationAccess, page, pageSize]);
  
  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / pageSize);
  }, [totalItems, pageSize]);
  
  // Fetch permissions
  const fetchPermissions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Log for debugging
      console.log("Fetching permissions data...");
      
      // Direct Supabase query for restaurant access - using explicit joins with structure tracking
      const { data: restaurantAccessData, error: restaurantError } = await supabase
        .from('user_restaurant_access')
        .select(`
          id,
          user_id,
          restaurant_id,
          access_level,
          profiles!user_id(
            id,
            name,
            email,
            role
          ),
          restaurants_master!restaurant_id(
            restaurant_id,
            restaurant_name,
            organisation_id,
            organisations!organisation_id(
              id,
              name
            )
          )
        `);
      
      if (restaurantError) {
        throw new Error(`Error fetching restaurant access: ${restaurantError.message}`);
      }
      
      // Direct Supabase query for organisation access with corrected join syntax
      const { data: organisationAccessData, error: organisationError } = await supabase
        .from('user_organisation_access')
        .select(`
          id,
          user_id,
          organisation_id,
          access_level,
          profiles!user_id(
            id,
            name,
            email,
            role
          ),
          organisations!organisation_id(
            id,
            name
          )
        `);
      
      if (organisationError) {
        throw new Error(`Error fetching organisation access: ${organisationError.message}`);
      }
      
      // Log the raw data for debugging
      console.log("Restaurant access raw data:", restaurantAccessData);
      console.log("Organisation access raw data:", organisationAccessData);
      
      // Format restaurant access data with correct nested path traversal
      const formattedRestaurantAccess = (restaurantAccessData || []).map(access => {
        // Add more detailed logging for the first item to understand the structure
        if (restaurantAccessData && restaurantAccessData.length > 0 && access.id === restaurantAccessData[0].id) {
          console.log("Sample access item structure:", JSON.stringify(access, null, 2));
        }

        // Extract user data - note the paths are corrected to match actual response structure
        const userData = access.profiles?.[0] || {};
        const restaurantData = access.restaurants_master?.[0] || {};
        const organisationData = restaurantData.organisations?.[0] || {};

        return {
          id: access.id,
          user_id: access.user_id,
          user_name: userData.name || 'Unknown User',
          user_email: userData.email || '',
          user_role: userData.role || 'user',
          restaurant_id: access.restaurant_id,
          restaurant_name: restaurantData.restaurant_name || 'Unknown Restaurant',
          organisation_id: restaurantData.organisation_id,
          organisation_name: organisationData.name || '',
          access_level: access.access_level
        };
      });
      
      // Format organisation access data with correct nested path traversal
      const formattedOrganisationAccess = (organisationAccessData || []).map(access => {
        // Extract user and organisation data
        const userData = access.profiles?.[0] || {};
        const organisationData = access.organisations?.[0] || {};

        return {
          id: access.id,
          user_id: access.user_id,
          user_name: userData.name || 'Unknown User',
          user_email: userData.email || '',
          user_role: userData.role || 'user',
          organisation_id: access.organisation_id,
          organisation_name: organisationData.name || 'Unknown Organisation',
          access_level: access.access_level
        };
      });
      
      // Log the formatted data
      console.log("Formatted restaurant access:", formattedRestaurantAccess);
      console.log("Formatted organisation access:", formattedOrganisationAccess);
      
      // Apply role filter if needed
      const filteredRestaurantAccess = roleFilter !== 'all' 
        ? formattedRestaurantAccess.filter(a => a.user_role === roleFilter)
        : formattedRestaurantAccess;
      
      const filteredOrganisationAccess = roleFilter !== 'all'
        ? formattedOrganisationAccess.filter(a => a.user_role === roleFilter)
        : formattedOrganisationAccess;
      
      setPermissions({
        restaurantAccess: filteredRestaurantAccess,
        organisationAccess: filteredOrganisationAccess
      });
      
    } catch (err: any) {
      console.error("Error fetching permissions:", err);
      setError(err.message || 'An error occurred while fetching permissions');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    // Only fetch permissions if no initial permissions data was provided
    if (!initialPermissions?.restaurantAccess?.length && !initialPermissions?.organisationAccess?.length) {
      fetchPermissions();
    } else {
      // If we have initial permissions, data enrichment is handled by the other useEffect
      // Just set loading to false once initialization is complete
      setIsLoading(false);
    }
  }, [initialPermissions]);
  
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, roleFilter, activeTab]);
  
  // Handle granting restaurant access
  const handleGrantRestaurantAccess = async () => {
    try {
      let result;
      
      // If multiple selection is enabled
      if (grantRestaurantForm.multiple_users || grantRestaurantForm.multiple_restaurants) {
        const payload = {
          user_ids: grantRestaurantForm.multiple_users 
            ? grantRestaurantForm.user_ids 
            : [grantRestaurantForm.user_id],
          restaurant_ids: grantRestaurantForm.multiple_restaurants
            ? grantRestaurantForm.restaurant_ids
            : [grantRestaurantForm.restaurant_id],
          access_level: grantRestaurantForm.access_level
        };
        
        result = await apiClient.post('/api/admin/permissions/bulk/restaurant', payload);
      } else {
        // Single user, single restaurant
        const payload = {
          user_id: grantRestaurantForm.user_id,
          restaurant_id: grantRestaurantForm.restaurant_id,
          access_level: grantRestaurantForm.access_level
        };
        
        result = await apiClient.post('/api/admin/permissions/restaurant', payload);
      }
      
      if (result.data?.success) {
        toast({
          title: 'Access Granted',
          description: result.data.message || 'Restaurant access granted successfully',
        });
        
        // Reset form and close dialog
        setGrantRestaurantForm({
          user_id: '',
          restaurant_id: '',
          access_level: 'admin',
          multiple_users: false,
          multiple_restaurants: false,
          user_ids: [],
          restaurant_ids: []
        });
        
        setGrantRestaurantDialogOpen(false);
        
        // Immediately enrich and update the new access records to avoid "Unknown" placeholders
        if (result.data.access_grants || result.data.access) {
          const newRecords = result.data.access_grants || [result.data.access];
          const enrichedRecords = await enrichAccessRecords(newRecords, 'restaurant');
          
          // Update the state with the new enriched records
          setPermissions(prevState => ({
            ...prevState,
            restaurantAccess: [...prevState.restaurantAccess, ...enrichedRecords]
          }));
        } else {
          // If we don't have the records, fall back to full refresh
          fetchPermissions();
        }
      } else {
        throw new Error(result.data?.error || 'Failed to grant restaurant access');
      }
    } catch (err: any) {
      
      toast({
        title: 'Error',
        description: err.message || 'Failed to grant restaurant access',
        variant: 'destructive',
      });
    }
  };
  
  // Helper function to enrich access records with user and entity details
  const enrichAccessRecords = async (records, type) => {
    try {
      // Get all unique user IDs and entity IDs from the records
      const userIds = [...new Set(records.map(record => record.user_id))];
      
      let restaurantIds = [];
      let organisationIds = [];
      
      if (type === 'restaurant') {
        restaurantIds = [...new Set(records.map(record => record.restaurant_id))];
        // Get potential organisation IDs from records if they exist
        organisationIds = [...new Set(records.filter(record => record.organisation_id).map(record => record.organisation_id))];
      } else {
        organisationIds = [...new Set(records.map(record => record.organisation_id))];
      }
      
      // Fetch user details
      const { data: users } = await supabase
        .from('profiles')
        .select('id, name, email, role')
        .in('id', userIds);
      
      // Create user lookup map
      const userMap = {};
      (users || []).forEach(user => {
        userMap[user.id] = user;
      });
      
      // Fetch entity details based on type
      const restaurantMap = {};
      const organisationMap = {};
      
      if (type === 'restaurant' && restaurantIds.length > 0) {
        const { data: restaurants } = await supabase
          .from('restaurants_master')
          .select('restaurant_id, restaurant_name, organisation_id')
          .in('restaurant_id', restaurantIds);
          
        (restaurants || []).forEach(restaurant => {
          restaurantMap[restaurant.restaurant_id] = restaurant;
          
          // Add any missing organisation IDs from restaurants
          if (restaurant.organisation_id && !organisationIds.includes(restaurant.organisation_id)) {
            organisationIds.push(restaurant.organisation_id);
          }
        });
      }
      
      if (organisationIds.length > 0) {
        const { data: organisations } = await supabase
          .from('organisations')
          .select('id, name')
          .in('id', organisationIds);
          
        (organisations || []).forEach(org => {
          organisationMap[org.id] = org;
        });
      }
      
      // Enrich records based on type
      if (type === 'restaurant') {
        return records.map(record => {
          const user = userMap[record.user_id] || {};
          const restaurant = restaurantMap[record.restaurant_id] || {};
          const organisationId = record.organisation_id || restaurant.organisation_id;
          const organisation = organisationId ? organisationMap[organisationId] : null;
          
          return {
            id: record.id,
            user_id: record.user_id,
            user_name: user.name || 'Unknown User',
            user_email: user.email || '',
            user_role: user.role || 'user',
            restaurant_id: record.restaurant_id,
            restaurant_name: restaurant.restaurant_name || 'Unknown Restaurant',
            organisation_id: organisationId,
            organisation_name: organisation ? organisation.name : '',
            access_level: record.access_level
          };
        });
      } else {
        return records.map(record => {
          const user = userMap[record.user_id] || {};
          const organisation = organisationMap[record.organisation_id] || {};
          
          return {
            id: record.id,
            user_id: record.user_id,
            user_name: user.name || 'Unknown User',
            user_email: user.email || '',
            user_role: user.role || 'user',
            organisation_id: record.organisation_id,
            organisation_name: organisation.name || 'Unknown Organisation',
            access_level: record.access_level
          };
        });
      }
    } catch (error) {
      console.error('Error enriching access records:', error);
      return records; // Return original records if enrichment fails
    }
  };

  // Handle granting organisation access
  const handleGrantOrganisationAccess = async () => {
    try {
      let result;
      
      // If multiple selection is enabled
      if (grantOrganisationForm.multiple_users) {
        const payload = {
          user_ids: grantOrganisationForm.user_ids,
          organisation_id: grantOrganisationForm.organisation_id,
          access_level: grantOrganisationForm.access_level
        };
        
        result = await apiClient.post('/api/admin/permissions/bulk/organisation', payload);
      } else {
        // Single user
        const payload = {
          user_id: grantOrganisationForm.user_id,
          organisation_id: grantOrganisationForm.organisation_id,
          access_level: grantOrganisationForm.access_level
        };
        
        result = await apiClient.post('/api/admin/permissions/organisation', payload);
      }
      
      if (result.data?.success) {
        toast({
          title: 'Access Granted',
          description: result.data.message || 'Organisation access granted successfully',
        });
        
        // Reset form and close dialog
        setGrantOrganisationForm({
          user_id: '',
          organisation_id: '',
          access_level: 'admin',
          multiple_users: false,
          user_ids: []
        });
        
        setGrantOrganisationDialogOpen(false);
        
        // Immediately enrich and update the new access records to avoid "Unknown" placeholders
        if (result.data.access_grants || result.data.access) {
          const newRecords = result.data.access_grants || [result.data.access];
          const enrichedRecords = await enrichAccessRecords(newRecords, 'organisation');
          
          // Update the state with the new enriched records
          setPermissions(prevState => ({
            ...prevState,
            organisationAccess: [...prevState.organisationAccess, ...enrichedRecords]
          }));
        } else {
          // If we don't have the records, fall back to full refresh
          fetchPermissions();
        }
      } else {
        throw new Error(result.data?.error || 'Failed to grant organisation access');
      }
    } catch (err: any) {
      
      toast({
        title: 'Error',
        description: err.message || 'Failed to grant organisation access',
        variant: 'destructive',
      });
    }
  };
  
  // Handle removing access
  const handleRemoveAccess = async () => {
    if (!selectedAccessToRemove) return;
    
    try {
      const { id, type } = selectedAccessToRemove;
      
      const result = await apiClient.delete(`/api/admin/permissions/${type}/${id}`);
      
      if (result.data?.success) {
        toast({
          title: 'Access Revoked',
          description: result.data.message || `${type.charAt(0).toUpperCase() + type.slice(1)} access revoked successfully`,
        });
        
        // Update local state immediately to avoid full refresh
        setPermissions(prevState => {
          if (type === 'restaurant') {
            return {
              ...prevState,
              restaurantAccess: prevState.restaurantAccess.filter(access => access.id !== id)
            };
          } else {
            return {
              ...prevState,
              organisationAccess: prevState.organisationAccess.filter(access => access.id !== id)
            };
          }
        });
        
        // Reset state and close dialog
        setSelectedAccessToRemove(null);
        setConfirmRemoveDialogOpen(false);
      } else {
        throw new Error(result.data?.error || `Failed to revoke ${type} access`);
      }
    } catch (err: any) {
      
      toast({
        title: 'Error',
        description: err.message || 'Failed to remove access',
        variant: 'destructive',
      });
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold">Access Permissions</h2>
        
        <div className="flex w-full sm:w-auto gap-2 flex-wrap">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
            <Input
              placeholder="Search permissions..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            defaultValue="all"
            value={roleFilter}
            onValueChange={(value) => setRoleFilter(value)}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="org_admin">Org Admin</SelectItem>
              <SelectItem value="rest_admin">Rest Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="new_sign_up">New Sign Up</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="mb-4 p-4 bg-muted rounded-md">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-medium mb-1">Understanding Access vs Roles</h3>
            <p className="text-sm text-muted-foreground">
              User roles (super_admin, org_admin, rest_admin, user) define what users can do, 
              while access assignments shown here determine which restaurants or organisations 
              they can access. Users can have multiple restaurant or organisation access permissions.
            </p>
          </div>
        </div>
      </div>
      
      <Tabs defaultValue="restaurant" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="restaurant">Restaurant Access</TabsTrigger>
          <TabsTrigger value="organisation">Organisation Access</TabsTrigger>
        </TabsList>
        
        <TabsContent value="restaurant" className="mt-4">
          <div className="flex justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              {totalItems} access records found
            </div>
            <Dialog open={grantRestaurantDialogOpen} onOpenChange={setGrantRestaurantDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Grant Restaurant Access
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]" aria-describedby="restaurant-access-dialog-description">
                <DialogHeader>
                  <DialogTitle>Grant Restaurant Access</DialogTitle>
                  <DialogDescription id="restaurant-access-dialog-description">
                    Give a user access to one or more restaurants.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Mode</Label>
                    <div className="flex gap-4">
                      <Label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={grantRestaurantForm.multiple_users}
                          onChange={(e) => setGrantRestaurantForm({
                            ...grantRestaurantForm,
                            multiple_users: e.target.checked,
                            user_id: e.target.checked ? '' : grantRestaurantForm.user_id,
                            user_ids: []
                          })}
                          className="h-4 w-4"
                        />
                        Multiple Users
                      </Label>
                      <Label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={grantRestaurantForm.multiple_restaurants}
                          onChange={(e) => setGrantRestaurantForm({
                            ...grantRestaurantForm,
                            multiple_restaurants: e.target.checked,
                            restaurant_id: e.target.checked ? '' : grantRestaurantForm.restaurant_id,
                            restaurant_ids: []
                          })}
                          className="h-4 w-4"
                        />
                        Multiple Restaurants
                      </Label>
                    </div>
                  </div>
                  
                  {/* Single or Multiple User Selection */}
                  {grantRestaurantForm.multiple_users ? (
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="users" className="text-right">
                        Users
                      </Label>
                      <Select
                        value={grantRestaurantForm.user_ids.length ? 'selected' : ''}
                        onValueChange={() => {}}
                      >
                        <SelectTrigger id="users" className="col-span-3">
                          <SelectValue placeholder={`${grantRestaurantForm.user_ids.length} users selected`} />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="p-2">
                            <div className="mb-2">
                              <Label className="text-xs text-muted-foreground">Select Users</Label>
                            </div>
                            <div className="max-h-[200px] overflow-auto">
                              {filteredUsers.map(user => (
                                <Label key={user.id} className="flex items-start gap-2 py-1">
                                  <input
                                    type="checkbox"
                                    checked={grantRestaurantForm.user_ids.includes(user.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setGrantRestaurantForm({
                                          ...grantRestaurantForm,
                                          user_ids: [...grantRestaurantForm.user_ids, user.id]
                                        });
                                      } else {
                                        setGrantRestaurantForm({
                                          ...grantRestaurantForm,
                                          user_ids: grantRestaurantForm.user_ids.filter(id => id !== user.id)
                                        });
                                      }
                                    }}
                                    className="h-4 w-4 mt-1"
                                  />
                                  <div className="ml-2">
                                    <div>
                                      {user.name} {user.role && (
                                        <Badge variant="outline" className="ml-1">
                                          {getRoleDisplayName(user.role)}
                                        </Badge>
                                      )}
                                    </div>
                                    {user.email && (
                                      <div className="text-xs text-muted-foreground">
                                        {user.email}
                                      </div>
                                    )}
                                    {hasExistingAccess(user.id, 'restaurant') && (
                                      <div className="text-xs text-primary">
                                        Has existing restaurant access
                                      </div>
                                    )}
                                  </div>
                                </Label>
                              ))}
                            </div>
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="user" className="text-right">
                        User
                      </Label>
                      <Select
                        value={grantRestaurantForm.user_id}
                        onValueChange={(value) => setGrantRestaurantForm({
                          ...grantRestaurantForm,
                          user_id: value
                        })}
                      >
                        <SelectTrigger id="user" className="col-span-3">
                          <SelectValue placeholder="Select user">
                            {grantRestaurantForm.user_id && (() => {
                              const selectedUser = filteredUsers.find(u => u.id === grantRestaurantForm.user_id);
                              return selectedUser ? `${selectedUser.name}${selectedUser.email ? ` (${selectedUser.email})` : ''}` : 'Select user';
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {filteredUsers.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              <div className="flex flex-col">
                                <span>{user.name} {user.role && (
                                  <Badge variant="outline" className="ml-1">
                                    {getRoleDisplayName(user.role)}
                                  </Badge>
                                )}</span>
                                {user.email && (
                                  <span className="text-xs text-muted-foreground">
                                    {user.email}
                                  </span>
                                )}
                                {hasExistingAccess(user.id, 'restaurant') && (
                                  <span className="text-xs text-primary mt-1">
                                    Has existing restaurant access
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Single or Multiple Restaurant Selection */}
                  {grantRestaurantForm.multiple_restaurants ? (
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="restaurants" className="text-right">
                        Restaurants
                      </Label>
                      <Select
                        value={grantRestaurantForm.restaurant_ids.length ? 'selected' : ''}
                        onValueChange={() => {}}
                      >
                        <SelectTrigger id="restaurants" className="col-span-3">
                          <SelectValue placeholder={`${grantRestaurantForm.restaurant_ids.length} restaurants selected`} />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="p-2">
                            <div className="mb-2">
                              <Label className="text-xs text-muted-foreground">Select Restaurants</Label>
                            </div>
                            <div className="max-h-[200px] overflow-auto">
                              {restaurants.map(restaurant => (
                                <Label key={restaurant.restaurant_id} className="flex items-center gap-2 py-1">
                                  <input
                                    type="checkbox"
                                    checked={grantRestaurantForm.restaurant_ids.includes(restaurant.restaurant_id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setGrantRestaurantForm({
                                          ...grantRestaurantForm,
                                          restaurant_ids: [...grantRestaurantForm.restaurant_ids, restaurant.restaurant_id]
                                        });
                                      } else {
                                        setGrantRestaurantForm({
                                          ...grantRestaurantForm,
                                          restaurant_ids: grantRestaurantForm.restaurant_ids.filter(id => id !== restaurant.restaurant_id)
                                        });
                                      }
                                    }}
                                    className="h-4 w-4"
                                  />
                                  <span className="ml-2">{restaurant.restaurant_name}</span>
                                </Label>
                              ))}
                            </div>
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="restaurant" className="text-right">
                        Restaurant
                      </Label>
                      <Select
                        value={grantRestaurantForm.restaurant_id}
                        onValueChange={(value) => setGrantRestaurantForm({
                          ...grantRestaurantForm,
                          restaurant_id: value
                        })}
                      >
                        <SelectTrigger id="restaurant" className="col-span-3">
                          <SelectValue placeholder="Select restaurant" />
                        </SelectTrigger>
                        <SelectContent>
                          {restaurants.map(restaurant => (
                            <SelectItem key={restaurant.restaurant_id} value={restaurant.restaurant_id}>
                              {restaurant.restaurant_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="access-level" className="text-right">
                      Access Level
                    </Label>
                    <Select
                      value={grantRestaurantForm.access_level}
                      onValueChange={(value) => setGrantRestaurantForm({
                        ...grantRestaurantForm,
                        access_level: value
                      })}
                    >
                      <SelectTrigger id="access-level" className="col-span-3">
                        <SelectValue placeholder="Select access level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin Access</SelectItem>
                        <SelectItem value="edit">Edit Access</SelectItem>
                        <SelectItem value="view">View Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="p-3 bg-muted rounded-md mb-4">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {grantRestaurantForm.multiple_users && grantRestaurantForm.multiple_restaurants ? (
                          <>
                            You're about to grant <strong>{grantRestaurantForm.user_ids.length} users</strong> access to <strong>{grantRestaurantForm.restaurant_ids.length} restaurants</strong>. This will create {grantRestaurantForm.user_ids.length * grantRestaurantForm.restaurant_ids.length} access records.
                          </>
                        ) : grantRestaurantForm.multiple_users ? (
                          <>
                            You're about to grant <strong>{grantRestaurantForm.user_ids.length} users</strong> access to the selected restaurant.
                          </>
                        ) : grantRestaurantForm.multiple_restaurants ? (
                          <>
                            You're about to grant the selected user access to <strong>{grantRestaurantForm.restaurant_ids.length} restaurants</strong>.
                          </>
                        ) : (
                          <>
                            You're about to grant the selected user access to the selected restaurant.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setGrantRestaurantDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleGrantRestaurantAccess}
                    disabled={
                      (grantRestaurantForm.multiple_users ? grantRestaurantForm.user_ids.length === 0 : !grantRestaurantForm.user_id) ||
                      (grantRestaurantForm.multiple_restaurants ? grantRestaurantForm.restaurant_ids.length === 0 : !grantRestaurantForm.restaurant_id)
                    }
                  >
                    Grant Access
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Restaurant</TableHead>
                    <TableHead>Access Level</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array(5).fill(0).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                        <TableCell className="text-right"><Skeleton className="h-6 w-8 ml-auto" /></TableCell>
                      </TableRow>
                    ))
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4 text-destructive">
                        Error loading permissions: {error}
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4">
                        No restaurant access records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((access: any) => (
                      <TableRow key={access.id} className="group">
                        <TableCell>
                          <div className="font-medium">{access.user_name}</div>
                          <div className="text-sm text-muted-foreground">{access.user_email}</div>
                          <div className="mt-1">
                            <Badge variant={getRoleBadgeVariant(access.user_role)} className="text-xs">
                              {getRoleDisplayName(access.user_role)}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>{access.restaurant_name}</TableCell>
                        <TableCell>
                          <Badge variant={
                            access.access_level === 'admin' ? 'outline' : 'secondary'
                          }>
                            {access.access_level === 'admin' ? 'Admin Access' : 
                             access.access_level === 'edit' ? 'Edit Access' : 'View Only'}
                          </Badge>
                        </TableCell>
                        <TableCell>{access.organisation_name || '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                            onClick={() => {
                              setSelectedAccessToRemove({
                                id: access.id,
                                type: 'restaurant',
                                name: access.restaurant_name,
                                userName: access.user_name
                              });
                              setConfirmRemoveDialogOpen(true);
                            }}
                          >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Remove access</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination controls */}
              {!isLoading && !error && totalItems > 0 && (
                <div className="flex items-center justify-between px-4 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {paginatedData.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalItems)} of {totalItems} records
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="text-sm">
                      Page {page} of {totalPages || 1}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="organisation" className="mt-4">
          <div className="flex justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              {totalItems} access records found
            </div>
            
            <Dialog open={grantOrganisationDialogOpen} onOpenChange={setGrantOrganisationDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Grant Organisation Access
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]" aria-describedby="organisation-access-dialog-description">
                <DialogHeader>
                  <DialogTitle>Grant Organisation Access</DialogTitle>
                  <DialogDescription id="organisation-access-dialog-description">
                    Give one or more users access to an organisation.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Mode</Label>
                    <Label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={grantOrganisationForm.multiple_users}
                        onChange={(e) => setGrantOrganisationForm({
                          ...grantOrganisationForm,
                          multiple_users: e.target.checked,
                          user_id: e.target.checked ? '' : grantOrganisationForm.user_id,
                          user_ids: []
                        })}
                        className="h-4 w-4"
                      />
                      Multiple Users
                    </Label>
                  </div>
                  
                  {/* Single or Multiple User Selection */}
                  {grantOrganisationForm.multiple_users ? (
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="org-users" className="text-right">
                        Users
                      </Label>
                      <Select
                        value={grantOrganisationForm.user_ids.length ? 'selected' : ''}
                        onValueChange={() => {}}
                      >
                        <SelectTrigger id="org-users" className="col-span-3">
                          <SelectValue placeholder={`${grantOrganisationForm.user_ids.length} users selected`} />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="p-2">
                            <div className="mb-2">
                              <Label className="text-xs text-muted-foreground">Select Users</Label>
                            </div>
                            <div className="max-h-[200px] overflow-auto">
                              {filteredUsers.map(user => (
                                <Label key={user.id} className="flex items-start gap-2 py-1">
                                  <input
                                    type="checkbox"
                                    checked={grantOrganisationForm.user_ids.includes(user.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setGrantOrganisationForm({
                                          ...grantOrganisationForm,
                                          user_ids: [...grantOrganisationForm.user_ids, user.id]
                                        });
                                      } else {
                                        setGrantOrganisationForm({
                                          ...grantOrganisationForm,
                                          user_ids: grantOrganisationForm.user_ids.filter(id => id !== user.id)
                                        });
                                      }
                                    }}
                                    className="h-4 w-4 mt-1"
                                  />
                                  <div className="ml-2">
                                    <div>
                                      {user.name} {user.role && (
                                        <Badge variant="outline" className="ml-1">
                                          {getRoleDisplayName(user.role)}
                                        </Badge>
                                      )}
                                    </div>
                                    {user.email && (
                                      <div className="text-xs text-muted-foreground">
                                        {user.email}
                                      </div>
                                    )}
                                    {hasExistingAccess(user.id, 'organisation') && (
                                      <div className="text-xs text-primary">
                                        Has existing organisation access
                                      </div>
                                    )}
                                  </div>
                                </Label>
                              ))}
                            </div>
                          </div>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="org-user" className="text-right">
                        User
                      </Label>
                      <Select
                        value={grantOrganisationForm.user_id}
                        onValueChange={(value) => setGrantOrganisationForm({
                          ...grantOrganisationForm,
                          user_id: value
                        })}
                      >
                        <SelectTrigger id="org-user" className="col-span-3">
                          <SelectValue placeholder="Select user">
                            {grantOrganisationForm.user_id && (() => {
                              const selectedUser = filteredUsers.find(u => u.id === grantOrganisationForm.user_id);
                              return selectedUser ? `${selectedUser.name}${selectedUser.email ? ` (${selectedUser.email})` : ''}` : 'Select user';
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {filteredUsers.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              <div className="flex flex-col">
                                <span>{user.name} {user.role && (
                                  <Badge variant="outline" className="ml-1">
                                    {getRoleDisplayName(user.role)}
                                  </Badge>
                                )}</span>
                                {user.email && (
                                  <span className="text-xs text-muted-foreground">
                                    {user.email}
                                  </span>
                                )}
                                {hasExistingAccess(user.id, 'organisation') && (
                                  <span className="text-xs text-primary mt-1">
                                    Has existing organisation access
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="organisation" className="text-right">
                      Organisation
                    </Label>
                    <Select
                      value={grantOrganisationForm.organisation_id}
                      onValueChange={(value) => setGrantOrganisationForm({
                        ...grantOrganisationForm,
                        organisation_id: value
                      })}
                    >
                      <SelectTrigger id="organisation" className="col-span-3">
                        <SelectValue placeholder="Select organisation" />
                      </SelectTrigger>
                      <SelectContent>
                        {organisations.map(org => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="org-access-level" className="text-right">
                      Access Level
                    </Label>
                    <Select
                      value={grantOrganisationForm.access_level}
                      onValueChange={(value) => setGrantOrganisationForm({
                        ...grantOrganisationForm,
                        access_level: value
                      })}
                    >
                      <SelectTrigger id="org-access-level" className="col-span-3">
                        <SelectValue placeholder="Select access level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin Access</SelectItem>
                        <SelectItem value="edit">Edit Access</SelectItem>
                        <SelectItem value="view">View Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="p-3 bg-muted rounded-md mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {grantOrganisationForm.multiple_users ? (
                          <>
                            You're about to grant <strong>{grantOrganisationForm.user_ids.length} users</strong> access to the selected organisation. If any of these users have the <strong>org_admin</strong> role, their profile organisation will be updated.
                          </>
                        ) : (
                          <>
                            You're about to grant the selected user access to the selected organisation. If the user has the <strong>org_admin</strong> role, their profile organisation will be updated.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setGrantOrganisationDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleGrantOrganisationAccess}
                    disabled={
                      (grantOrganisationForm.multiple_users ? grantOrganisationForm.user_ids.length === 0 : !grantOrganisationForm.user_id) ||
                      !grantOrganisationForm.organisation_id
                    }
                  >
                    Grant Access
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Access Level</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array(5).fill(0).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                        <TableCell className="text-right"><Skeleton className="h-6 w-8 ml-auto" /></TableCell>
                      </TableRow>
                    ))
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-4 text-destructive">
                        Error loading permissions: {error}
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-4">
                        No organisation access records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((access: any) => (
                      <TableRow key={access.id} className="group">
                        <TableCell>
                          <div className="font-medium">{access.user_name}</div>
                          <div className="text-sm text-muted-foreground">{access.user_email}</div>
                          <div className="mt-1">
                            <Badge variant={getRoleBadgeVariant(access.user_role)} className="text-xs">
                              {getRoleDisplayName(access.user_role)}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>{access.organisation_name}</TableCell>
                        <TableCell>
                          <Badge variant={
                            access.access_level === 'admin' ? 'outline' : 'secondary'
                          }>
                            {access.access_level === 'admin' ? 'Admin Access' : 
                             access.access_level === 'edit' ? 'Edit Access' : 'View Only'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                            onClick={() => {
                              setSelectedAccessToRemove({
                                id: access.id,
                                type: 'organisation',
                                name: access.organisation_name,
                                userName: access.user_name
                              });
                              setConfirmRemoveDialogOpen(true);
                            }}
                          >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Remove access</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination controls */}
              {!isLoading && !error && totalItems > 0 && (
                <div className="flex items-center justify-between px-4 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {paginatedData.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalItems)} of {totalItems} records
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="text-sm">
                      Page {page} of {totalPages || 1}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Confirm Remove Access Dialog */}
      <Dialog open={confirmRemoveDialogOpen} onOpenChange={setConfirmRemoveDialogOpen}>
        <DialogContent className="sm:max-w-[425px]" aria-describedby="remove-access-dialog-description">
          <DialogHeader>
            <DialogTitle>Remove Access</DialogTitle>
            <DialogDescription id="remove-access-dialog-description">
              Are you sure you want to remove this access permission?
            </DialogDescription>
          </DialogHeader>
          
          {selectedAccessToRemove && (
            <div className="py-4">
              <p className="mb-2">You are about to remove access for:</p>
              <div className="p-3 border rounded-md">
                <p><strong>User:</strong> {selectedAccessToRemove.userName}</p>
                <p>
                  <strong>{selectedAccessToRemove.type === 'restaurant' ? 'Restaurant' : 'Organisation'}:</strong> {selectedAccessToRemove.name}
                </p>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The user will no longer have access to this {selectedAccessToRemove.type}.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setConfirmRemoveDialogOpen(false);
                setSelectedAccessToRemove(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRemoveAccess}
            >
              Remove Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPermissionsTab;